import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { BranchContext } from './BranchContext';
import { syncInputElementValue } from '../utils/syncInputElementValue';

// ─── Data context (menuItems, tables, orders) ───────────────────────────────
const POSDataContext = createContext();

export const usePOSData = () => {
  const context = useContext(POSDataContext);
  if (!context) {
    throw new Error('usePOSData must be used within a POSProvider');
  }
  return context;
};

// ─── Keyboard / UI context ───────────────────────────────────────────────────
const POSKeyboardContext = createContext();

export const usePOSKeyboard = () => {
  const context = useContext(POSKeyboardContext);
  if (!context) {
    throw new Error('usePOSKeyboard must be used within a POSProvider');
  }
  return context;
};

// ─── Combined alias — keeps all existing consumers working unchanged ─────────
export const usePOS = () => ({
  ...usePOSData(),
  ...usePOSKeyboard(),
});

// ─── Provider ────────────────────────────────────────────────────────────────
export const POSProvider = ({ children }) => {
  const { selectedBranch } = useContext(BranchContext);

  // Data state
  const [menuItems, setMenuItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);

  // Touch keyboard state
  const [touchKeyboardEnabled, setTouchKeyboardEnabled] = useState(() => {
    const saved = localStorage.getItem('pos_touch_keyboard_enabled');
    return saved ? JSON.parse(saved) : false;
  });
  const [showTouchKeyboard, setShowTouchKeyboard] = useState(false);
  const [keyboardTargetInput, setKeyboardTargetInput] = useState(null);
  const [focusedInputData, setFocusedInputData] = useState(null);
  const [keyboardInputValue, setKeyboardInputValue] = useState('');
  const [keyboardCursorPosition, setKeyboardCursorPosition] = useState(0);
  const [isClosingKeyboard, setIsClosingKeyboard] = useState(false);

  // Save keyboard preference to localStorage
  useEffect(() => {
    localStorage.setItem('pos_touch_keyboard_enabled', JSON.stringify(touchKeyboardEnabled));
  }, [touchKeyboardEnabled]);

  // ─── Data functions ──────────────────────────────────────────────────────
  const updatePOSData = useCallback((data) => {
    if (data.menuItems !== undefined) {
      setMenuItems(data.menuItems);
    }
    if (data.tables !== undefined) {
      setTables(data.tables);
    }
    if (data.activeOrders !== undefined) {
      setActiveOrders(data.activeOrders);
    }
  }, []);

  // ─── Keyboard functions ──────────────────────────────────────────────────
  const toggleTouchKeyboard = useCallback(() => {
    setTouchKeyboardEnabled(prev => !prev);
  }, []);

  const enableTouchKeyboard = useCallback(() => {
    setTouchKeyboardEnabled(true);
  }, []);

  const openTouchKeyboard = useCallback((inputElement) => {
    if (isClosingKeyboard) {
      return;
    }

    if (inputElement && inputElement.hasAttribute('data-focused-input-overlay')) {
      return;
    }

    if (touchKeyboardEnabled) {
      const rect = inputElement.getBoundingClientRect();
      const inputData = {
        element: inputElement,
        value: inputElement.value || '',
        placeholder: inputElement.placeholder || '',
        type: inputElement.type || 'text',
        rect: {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        }
      };

      const initialValue = inputElement.value || '';
      setKeyboardTargetInput(inputElement);
      setFocusedInputData(inputData);
      setKeyboardInputValue(initialValue);
      setKeyboardCursorPosition(initialValue.length);
      setShowTouchKeyboard(true);

      const posContainer = document.querySelector('[data-pos-container="true"]');
      if (posContainer) {
        const children = Array.from(posContainer.children);
        children.forEach(child => {
          const hasKeyboardAttr = child.hasAttribute('data-touch-keyboard');
          const hasKeyboardChild = child.querySelector('[data-touch-keyboard]');
          const isOverlay = window.getComputedStyle(child).zIndex > 9999;

          if (!hasKeyboardAttr && !hasKeyboardChild && !isOverlay) {
            child.style.opacity = '0.4';
            child.style.transition = 'opacity 0.3s ease';
            child.classList.add('pos-blurred');
          }
        });
      }
    }
  }, [isClosingKeyboard, touchKeyboardEnabled]);

  const closeTouchKeyboard = useCallback((valueOverride) => {
    const targetInput = keyboardTargetInput;
    const finalValue = valueOverride !== undefined ? valueOverride : keyboardInputValue;

    setIsClosingKeyboard(true);
    setShowTouchKeyboard(false);
    setKeyboardTargetInput(null);
    setFocusedInputData(null);

    if (targetInput && finalValue !== undefined) {
      syncInputElementValue(targetInput, finalValue);
    }

    setTimeout(() => {
      setIsClosingKeyboard(false);
    }, 300);

    const posContainer = document.querySelector('[data-pos-container="true"]');
    if (posContainer) {
      const blurredElements = posContainer.querySelectorAll('.pos-blurred');
      blurredElements.forEach(element => {
        element.style.opacity = '';
        element.classList.remove('pos-blurred');
      });
    }

    setTimeout(() => {
      setKeyboardInputValue('');
      setKeyboardCursorPosition(0);
    }, 500);
  }, [keyboardTargetInput, keyboardInputValue]);

  const closeTouchKeyboardWithValue = useCallback((finalValue) => {
    closeTouchKeyboard(finalValue);
  }, [closeTouchKeyboard]);

  // ─── Memoized context values (each context re-renders only its subscribers) ─
  const dataValue = useMemo(() => ({
    selectedBranch: selectedBranch?._id || selectedBranch,
    menuItems,
    tables,
    activeOrders,
    updatePOSData,
    setMenuItems,
    setTables,
    setActiveOrders,
  }), [
    selectedBranch,
    menuItems,
    tables,
    activeOrders,
    updatePOSData,
  ]);

  const keyboardValue = useMemo(() => ({
    touchKeyboardEnabled,
    showTouchKeyboard,
    keyboardTargetInput,
    focusedInputData,
    keyboardInputValue,
    setKeyboardInputValue,
    keyboardCursorPosition,
    setKeyboardCursorPosition,
    toggleTouchKeyboard,
    enableTouchKeyboard,
    openTouchKeyboard,
    closeTouchKeyboard,
    closeTouchKeyboardWithValue,
  }), [
    touchKeyboardEnabled,
    showTouchKeyboard,
    keyboardTargetInput,
    focusedInputData,
    keyboardInputValue,
    keyboardCursorPosition,
    toggleTouchKeyboard,
    enableTouchKeyboard,
    openTouchKeyboard,
    closeTouchKeyboard,
    closeTouchKeyboardWithValue,
  ]);

  return (
    <POSDataContext.Provider value={dataValue}>
      <POSKeyboardContext.Provider value={keyboardValue}>
        {children}
      </POSKeyboardContext.Provider>
    </POSDataContext.Provider>
  );
};
